<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useStore } from 'vuex';
import { useCrmReportsStore } from 'dashboard/stores/crmReports';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const route = useRoute();
const router = useRouter();
const vuexStore = useStore();
const reportsStore = useCrmReportsStore();
const { filters } = storeToRefs(reportsStore);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isOriginDashEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CRM_ORIGIN_DASHBOARD
  )
);

const account = useMapGetter('getCurrentAccount');
const funnels = computed(
  () => account.value?.custom_attributes?.crm_funnels || []
);
// `accounts/getAccount`, e NÃO o `getCurrentAccount` do auth: o do auth é a
// entrada da conta na lista do usuário e traz só id, nome, papel e permissões.
// `crm_mode` só existe no registro completo da conta.
const accountById = useMapGetter('accounts/getAccount');
const fullAccount = computed(() => accountById.value(accountId.value));

// O montador e o painel leem `crm_deals`. Em modo conversational o lead É a
// conversa com etiqueta de etapa, então as duas abas mostrariam zero (ou, pior,
// restos de migração em crm_deals que não batem com o quadro).
const isDealsMode = computed(() => fullAccount.value?.crm_mode === 'deals');

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const conversationAttributes = useMapGetter(
  'attributes/getConversationAttributes'
);
// Campo da conversa do tipo lista vira filtro ao lado da caixa (ex.: "Marca" na
// Marajó, onde várias marcas dividem o mesmo número). Texto livre não entra:
// sem lista fechada de valores o filtro viraria campo de digitação.
const listAttributes = computed(() =>
  (conversationAttributes.value || []).filter(
    attr => attr.attributeDisplayType === 'list' && attr.attributeValues?.length
  )
);
const ATTR_QUERY_PREFIX = 'attr_';

const LABELS = {
  title: 'Relatórios CRM',
  subtitle: 'Performance de vendas e origens dos leads',
  comparePrevious: 'Comparar período anterior',
  allInboxes: 'Todas as caixas',
  allAgents: 'Todos os agentes',
  allValues: 'todas',
  periodSeparator: 'até',
  tabs: {
    general: 'Geral',
    origin: 'Por Origem',
    platform: 'Por Plataforma',
    ad: 'Por Anúncio',
    dashboard: 'Painel',
    custom: 'Personalizados',
  },
};

// O período pode recortar pela data de criação ou pela de fechamento. Por
// criação (padrão) responde "quantos leads entraram e o que virou"; por
// fechamento responde "o que fechou neste mês", que é o número que quem vende
// espera bater no fim do mês.
const DATE_FIELDS = [
  { value: 'created', label: 'Por data de criação' },
  { value: 'closed', label: 'Por data de fechamento' },
];

const PERIODS = [
  { value: 'today', label: 'Hoje' },
  { value: 'current_month', label: 'Mês atual' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'all', label: 'Todo o período' },
  { value: 'custom', label: 'Personalizado' },
];

const tabs = computed(() => {
  const base = [{ name: 'crm_reports', label: LABELS.tabs.general }];
  if (isOriginDashEnabled.value) {
    base.push(
      { name: 'crm_reports_origin', label: LABELS.tabs.origin },
      { name: 'crm_reports_platform', label: LABELS.tabs.platform },
      { name: 'crm_reports_ad', label: LABELS.tabs.ad }
    );
  }
  // Fora do feature flag de propósito (as abas acima dependem de rastreio de
  // anúncio, o montador não), mas presas ao modo de negociações.
  if (isDealsMode.value) {
    base.push(
      { name: 'crm_reports_dashboard', label: LABELS.tabs.dashboard },
      { name: 'crm_reports_custom', label: LABELS.tabs.custom }
    );
  }
  return base;
});

const activeTab = computed(() => route.name);

const goTo = name =>
  router.push({ name, params: { accountId: route.params.accountId } });

const updateFilter = (key, value) => reportsStore.setFilter(key, value);
const updateConvAttr = (key, value) => reportsStore.setConvAttr(key, value);
const allValuesLabel = attr =>
  `${attr.attributeDisplayName}: ${LABELS.allValues}`;

onMounted(() => {
  vuexStore.dispatch('inboxes/get');
  vuexStore.dispatch('agents/get');
  vuexStore.dispatch('attributes/get');
  const q = route.query;
  Object.entries(q)
    .filter(([key, value]) => key.startsWith(ATTR_QUERY_PREFIX) && value)
    .forEach(([key, value]) =>
      reportsStore.setConvAttr(key.slice(ATTR_QUERY_PREFIX.length), value)
    );
  if (q.period) reportsStore.setFilter('period', q.period);
  if (q.date_field) reportsStore.setFilter('dateField', q.date_field);
  if (q.funnel_id) reportsStore.setFilter('funnelId', q.funnel_id);
  if (q.inbox_id) reportsStore.setFilter('inboxId', Number(q.inbox_id));
  if (q.agent_id) reportsStore.setFilter('agentId', Number(q.agent_id));
  if (q.compare === '1') reportsStore.setFilter('comparePrevious', true);
  if (!filters.value.funnelId && funnels.value.length) {
    reportsStore.setFilter('funnelId', funnels.value[0].id);
  }
});

watch(
  filters,
  f => {
    const query = {
      period: f.period,
      ...(f.dateField && f.dateField !== 'created'
        ? { date_field: f.dateField }
        : {}),
      ...(f.funnelId ? { funnel_id: f.funnelId } : {}),
      ...(f.inboxId ? { inbox_id: f.inboxId } : {}),
      ...(f.agentId ? { agent_id: f.agentId } : {}),
      ...Object.fromEntries(
        Object.entries(f.convAttrs || {}).map(([key, value]) => [
          `${ATTR_QUERY_PREFIX}${key}`,
          value,
        ])
      ),
      ...(f.comparePrevious ? { compare: '1' } : {}),
      ...(f.period === 'custom' && f.dateFrom ? { date_from: f.dateFrom } : {}),
      ...(f.period === 'custom' && f.dateTo ? { date_to: f.dateTo } : {}),
    };
    router.replace({ query }).catch(() => {});
  },
  { deep: true }
);
</script>

<template>
  <!-- `w-full` como nas outras telas do CRM: sem ele o contêiner encolhe pro
       tamanho do conteúdo dentro do flex do shell e sobra faixa vazia à
       direita. `min-w-0` para a tabela larga poder rolar dentro dela em vez de
       empurrar a pagina. -->
  <div class="flex flex-col h-full w-full min-w-0 min-h-0 bg-n-background">
    <header
      class="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-2 border-b border-n-weak bg-n-surface-1"
    >
      <h1 class="text-base font-bold text-n-slate-12 m-0 whitespace-nowrap">
        {{ LABELS.title }}
      </h1>

      <nav class="flex items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.name"
          class="px-3 py-1.5 text-xs font-medium border-0 bg-transparent cursor-pointer transition-colors rounded-md whitespace-nowrap"
          :class="
            activeTab === tab.name
              ? 'text-woot-500 bg-woot-50 dark:bg-woot-500/10'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="goTo(tab.name)"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div class="flex-1" />

      <div class="flex flex-wrap items-center gap-2">
        <select
          :value="filters.period"
          class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer max-w-[140px]"
          @change="updateFilter('period', $event.target.value)"
        >
          <option v-for="p in PERIODS" :key="p.value" :value="p.value">
            {{ p.label }}
          </option>
        </select>

        <select
          :value="filters.dateField || 'created'"
          class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer max-w-[170px]"
          @change="updateFilter('dateField', $event.target.value)"
        >
          <option v-for="d in DATE_FIELDS" :key="d.value" :value="d.value">
            {{ d.label }}
          </option>
        </select>

        <template v-if="filters.period === 'custom'">
          <input
            type="date"
            :value="filters.dateFrom"
            class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 max-w-[130px]"
            @change="updateFilter('dateFrom', $event.target.value)"
          />
          <span class="text-xs text-n-slate-11">{{
            LABELS.periodSeparator
          }}</span>
          <input
            type="date"
            :value="filters.dateTo"
            class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 max-w-[130px]"
            @change="updateFilter('dateTo', $event.target.value)"
          />
        </template>

        <select
          v-if="funnels.length > 1"
          :value="filters.funnelId"
          class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer max-w-[140px]"
          @change="updateFilter('funnelId', $event.target.value)"
        >
          <option v-for="f in funnels" :key="f.id" :value="f.id">
            {{ f.name }}
          </option>
        </select>

        <select
          :value="filters.inboxId || ''"
          class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer max-w-[150px]"
          @change="
            updateFilter(
              'inboxId',
              $event.target.value ? Number($event.target.value) : null
            )
          "
        >
          <option value="">{{ LABELS.allInboxes }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>

        <select
          v-for="attr in listAttributes"
          :key="attr.attributeKey"
          :value="filters.convAttrs?.[attr.attributeKey] || ''"
          class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer max-w-[150px]"
          @change="updateConvAttr(attr.attributeKey, $event.target.value)"
        >
          <option value="">{{ allValuesLabel(attr) }}</option>
          <option
            v-for="option in attr.attributeValues"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>

        <select
          :value="filters.agentId || ''"
          class="px-2 py-1 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer max-w-[150px]"
          @change="
            updateFilter(
              'agentId',
              $event.target.value ? Number($event.target.value) : null
            )
          "
        >
          <option value="">{{ LABELS.allAgents }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>

        <label
          class="flex items-center gap-1 text-xs text-n-slate-12 cursor-pointer whitespace-nowrap"
        >
          <input
            type="checkbox"
            :checked="filters.comparePrevious"
            class="w-3 h-3"
            @change="updateFilter('comparePrevious', $event.target.checked)"
          />
          {{ LABELS.comparePrevious }}
        </label>
      </div>
    </header>

    <main class="flex-1 min-h-0 overflow-y-auto">
      <router-view />
    </main>
  </div>
</template>
